import React from "react";
import { cn, formatDateToLocalInputValue } from "../../../../lib/utils";
import { NumericInput } from "../../../../components/ui/input/NumericInput";
import { History } from "lucide-react";
import { RadioGroup } from "radix-ui";
import { OptionNameValuePair } from "../../../../../src/lib/interfaces/types";
import SelectDropdownV2 from "../../../../../src/components/ui/selectdropdown/v2";


export interface EventDateInputProps {
  value?: string;
  onClick: () => void;
  disabled: boolean;
}

export const EventDateInputCell: React.FC<EventDateInputProps> = ({
  value,
  onClick,
  disabled = false,
}) => {
  const addEventModalButton = () => (
    <button
      type="button"
      className={cn(
        "h-8 w-8 flex items-center justify-center rounded-md",
        disabled ? "cursor-not-allowed" : "hover:bg-gray-50"
      )}
      onClick={onClick}
      disabled={disabled}
    >
      <History
        className={cn("h-4 w-4", disabled ? "text-gray-400" : "text-[#3551F3]")}
      />
    </button>
  );

  if (value) {
    return (
      <div className="w-full flex items-center justify-between">
        <span className="font-sans font-normal text-sm leading-5 text-[#0A0A0A]">
          {value}
        </span>
        {addEventModalButton()}
      </div>
    );
  }

  return (
    <div className="w-full flex items-center">
      <span className="flex-1 font-sans font-normal text-sm leading-5 text-[#737373]">
        Select date
      </span>
      {addEventModalButton()}
    </div>
  );
};

export interface TextAreaInputCellProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  disableResize?: boolean;
  rows?: number;
  textAreaClassName?: string;
}

export const TextAreaInputCell: React.FC<TextAreaInputCellProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Enter Status",
  disableResize = false,
  rows,
  textAreaClassName,
}) => {
  return (
    <div className="relative w-full min-h-[52px] flex items-center">
      {!value && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 font-sans font-normal text-sm leading-5 text-[#737373] pointer-events-none">
          {placeholder}
        </span>
      )}

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => {
          if (onBlur) {
            onBlur(e.target.value);
          }
        }}
        rows={rows}
        placeholder=""
        className={cn(
          "w-full min-h-[52px] bg-transparent",
          disableResize ? "resize-none" : "resize-y",
          "font-sans font-normal text-sm leading-5",
          value ? "text-[#0A0A0A]" : "text-transparent",
          "focus:text-[#0A0A0A]",
          "focus:outline-none",
          textAreaClassName
        )}
      />
    </div>
  );
};

interface DateInputCellProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  max?: string;
  className?: string;
  inputClassName?: string;
}

export const DateInputCell: React.FC<DateInputCellProps> = ({
  value,
  onChange,
  disabled = false,
  max,
  className,
  inputClassName,
}) => {
  return (
    <div className={cn("w-full min-h-[52px] flex items-center", className)}>
      <input
        type="date"
        value={formatDateToLocalInputValue(value)}
        disabled={disabled}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full p-2 text-sm font-normal",
          "border border-transparent rounded-md",
          "focus:outline-none focus:ring-0",
          "focus:bg-[#F3F4F6]",
          disabled && "cursor-not-allowed",
          inputClassName
        )}
      />
    </div>
  );
};


interface NumberInputCellProps {
  value?: number;
  onChange: (value: number | undefined) => void;
  decimals: number;
  placeholder?: string;
  disabled?: boolean;
}

export const NumberInputCell: React.FC<NumberInputCellProps> = ({
  value,
  onChange,
  decimals,
  placeholder = "Enter amount",
  disabled = false,
}) => {
  return (
    <div className="w-full min-h-[52px] flex items-center">
      <NumericInput
        value={value}
        onChange={onChange}
        decimals={decimals}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "w-full p-2 text-sm font-normal",
          "text-[#0A0A0A] placeholder:text-[#737373]",
          "bg-transparent border border-transparent rounded-md",
          "focus:outline-none focus:ring-0",
          "focus:bg-[#F3F4F6]",
          disabled && "cursor-not-allowed"
        )}
      />
    </div>
  );
};

export interface RadioOption<T extends string> {
  label: string;
  value: T;
  selectedBorderClass?: string;
  selectedBgClass?: string;
  selectedDotClass?: string;
}

export interface RadioGroupInputCellProps<T extends string> {
  value?: T;
  onChange?: (value: T) => void;
  options: RadioOption<T>[];
  disabled?: boolean;
  className?: string;
}

export function RadioGroupInputCell<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  className,
}: RadioGroupInputCellProps<T>) {
  return (
    <div className={cn("w-full min-h-[52px] flex items-center", className)}>
      <RadioGroup.Root
        value={value ?? ""}
        onValueChange={(val) => {
          if (!disabled && onChange) {
            onChange(val as T);
          }
        }}
        disabled={disabled}
        className="flex gap-4"
      >
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <div
              key={option.value}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all",
                "min-w-[120px]",
                isSelected
                  ? cn(
                      option.selectedBorderClass || "border-blue-600",
                      option.selectedBgClass || "bg-blue-50"
                    )
                  : "border-[#E5E5E5] bg-white",
                disabled && "cursor-not-allowed"
              )}
            >
              <RadioGroup.Item
                value={option.value}
                className={cn(
                  "w-4 h-4 rounded-full border flex items-center justify-center",
                  isSelected
                    ? option.selectedBorderClass || "border-blue-600"
                    : "border-gray-300"
                )}
              >
                <RadioGroup.Indicator
                  className={cn(
                    "w-2 h-2 rounded-full",
                    option.selectedDotClass || "bg-blue-600"
                  )}
                />
              </RadioGroup.Item>

              <span className="text-sm font-medium text-[#0A0A0A]">
                {option.label}
              </span>
            </div>
          );
        })}
      </RadioGroup.Root>
    </div>
  );
}

interface SelectInputCellProps<T> {
  value?: T;
  onChange: (value: T) => void;
  options: OptionNameValuePair<T>[];
  placeholder: string;
  disabled?: boolean;
  className?: string;
  dropdownClassName?: string;
}

export function SelectInputCell<T>({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  className,
  dropdownClassName,
}: SelectInputCellProps<T>) {
  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={cn("w-full min-w-0 min-h-[52px] flex items-center", className)}>
      <SelectDropdownV2
        name={placeholder || "Select"}
        placeholder={placeholder || "Select an option..."}
        options={options}
        selectedOption={selectedOption}
        enableSearch={false}
        onSelectionChange={(opt) => onChange(opt.value)}
        disabled={disabled}
        containerClassName={cn( "w-full min-w-0 relative", disabled && "cursor-not-allowed")}
        labelClassName={cn(
          "w-full text-sm font-normal",
          selectedOption ? "text-[#0A0A0A]" : "text-[#737373]",
          disabled? "!cursor-not-allowed": "!cursor-pointer"
        )}
        dropdownClassName={cn(
          "min-w-[300px] max-w-[500px]",
          dropdownClassName
        )}
      />
    </div>
  );
}



export type ReportFormRow = {
  label: string;
  inputCell: React.ReactNode;
  alignTop?: boolean;
  disabled?: boolean;
};

interface ReportFormTableProps {
  className?: string;
  rows: ReportFormRow[];
}

export const ReportFormTable: React.FC<ReportFormTableProps> = ({
  className,
  rows,
}) => {
  return (
    <div className={cn("w-full overflow-x-auto", className)}>
      <div className="flex flex-col border border-[#E5E5E5] rounded-lg overflow-hidden">
        {rows.map((row, idx) => {
          const isLast = idx === rows.length - 1;
          return (
            <div
              key={idx}
              className={cn(
                "flex w-full",
                !isLast && "border-b border-[#E5E5E5]",
              )}
            >
              <div
                className={cn(
                  "w-[322px] shrink-0 bg-[#F1F5F9] p-2",
                  "min-h-[52px]",
                  row.alignTop ? "flex items-start" : "flex items-center",
                  row.disabled && "opacity-50"
                )}
              >
                <span className="font-sans font-medium text-sm leading-5 text-[#0A0A0A]">
                  {row.label}
                </span>
              </div>

              <div
                className={cn(
                  "flex-1 min-w-0 bg-white p-2",
                  "min-h-[52px]",
                  row.alignTop ? "flex items-start" : "flex items-center",
                  row.disabled && "opacity-50"
                )}
              >
                {row.inputCell}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportFormTable;
